import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X } from "lucide-react";
import { api } from "@/lib/api";

const L = {
  fr: {
    badge: "Anniversaire de Cocoon",
    title: (y) => `${y} an${y > 1 ? "s" : ""} d'émerveillement`,
    body: (s) => `Il y a un an, tu ouvrais ce Cocoon. Depuis, tu as allumé ${s} étoile${s > 1 ? "s" : ""} de curiosité. L'Élan lève sa tasse à ta soif de comprendre.`,
    close: "Merci, L'Élan",
  },
  en: {
    badge: "Cocoon anniversary",
    title: (y) => `${y} year${y > 1 ? "s" : ""} of wonder`,
    body: (s) => `A year ago, you opened this Cocoon. Since then, you've lit ${s} star${s > 1 ? "s" : ""} of curiosity. L'Élan raises a cup to your thirst for understanding.`,
    close: "Thank you, L'Élan",
  },
  es: {
    badge: "Aniversario del Cocoon",
    title: (y) => `${y} año${y > 1 ? "s" : ""} de asombro`,
    body: (s) => `Hace un año abriste este Cocoon. Desde entonces has encendido ${s} estrella${s > 1 ? "s" : ""} de curiosidad. L'Élan brinda por tu sed de entender.`,
    close: "Gracias, L'Élan",
  },
  nl: {
    badge: "Cocoon-verjaardag",
    title: (y) => `${y} jaar verwondering`,
    body: (s) => `Een jaar geleden opende je deze Cocoon. Sindsdien heb je ${s} ster${s > 1 ? "ren" : ""} van nieuwsgierigheid ontstoken. L'Élan heft het glas op je dorst naar inzicht.`,
    close: "Dank je, L'Élan",
  },
};

export function AnniversaryModal() {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const t = L[lang] || L.fr;
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/anniversary")
      .then(({ data }) => {
        if (data.anniversary) {
          const key = `coccon_anniv_${new Date().getFullYear()}`;
          if (!localStorage.getItem(key)) setData(data);
        }
      })
      .catch(() => {});
  }, []);

  const close = () => {
    localStorage.setItem(`coccon_anniv_${new Date().getFullYear()}`, "1");
    setData(null);
  };

  return (
    <AnimatePresence>
      {data && (
        <motion.div
          className="fixed inset-0 z-[120] flex items-center justify-center p-6 bg-background/75 backdrop-blur-sm"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="anniversary-modal"
        >
          <motion.div
            initial={{ scale: 0.9, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", stiffness: 240, damping: 22 }}
            className="relative max-w-md w-full glass border border-primary/30 rounded-3xl p-8 text-center overflow-hidden"
          >
            <div className="anniv-confetti" aria-hidden />
            <button onClick={close} data-testid="anniv-close" className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full flex items-center justify-center text-foreground/50 hover:text-primary hover:bg-primary/10 transition-colors">
              <X size={18} />
            </button>
            <div className="relative">
              <div className="flex items-center justify-center gap-2 mb-4">
                <Sparkles className="text-primary pulse-star" size={20} />
                <p className="uppercase tracking-[0.22em] text-xs text-primary/90">{t.badge}</p>
              </div>
              <div className="anniv-orb mx-auto mb-6" aria-hidden />
              <h3 className="font-display italic text-4xl text-gradient-orange mb-4" data-testid="anniv-title">{t.title(data.years)}</h3>
              <p className="text-foreground/70 leading-relaxed mb-7">{t.body(data.stars)}</p>
              <button onClick={close} data-testid="anniv-done-btn" className="text-sm font-medium px-6 py-2.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
                {t.close}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
